// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Body of the alarm tabs: reads the derived console data (TanStack Query
// `select`), applies the group filter (`?group=`, shared across Home tabs — see
// AlarmTabs) + the sort from the alarmsViewStore, and
// renders every load state (Carbon skeleton / error / empty / data) in tile or
// list layout.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionableNotification, Button, SkeletonPlaceholder, SkeletonText } from '@carbon/react';
import { useConsoleAlarms } from '../../../hooks/useConsoleAlarms';
import { useAnomalies } from '../../../hooks/useAnomalies';
import { useAlarmsViewStore } from '../../../stores/alarmsViewStore';
import { useUrlState } from '../../../hooks/useUrlState';
import {
  alarmDetailPath,
  anomalyDetailPath,
  groupsOf,
  applyGroup,
  sortAlarms,
  type AlarmTabKey,
} from '../../../services/alarmSelectors';
import { EmptyState } from '../ScreenHelpers';
import { StaleDataNotice } from '../../common/StaleDataNotice';
import { AlarmCard } from './AlarmCard';
import { AssetTypeChips } from '../AssetTypeChips';
import { equipmentTypeDisplayName } from '../../../utils/equipmentTypeLabels';

/**
 * Rows revealed per "Load more" click (and on first render), on every tab.
 *
 * Purely a rendering budget: both sources now fetch every cursor page up front
 * (the severity ordering has to be computed over the full set), so this bounds
 * the DOM, not the network. Alarms can run to four figures at a busy site.
 */
const LIST_PAGE_STEP = 30;

/** Tile-shaped loading placeholder that matches the card grid. */
function CardSkeleton() {
  return (
    <div className="hs-alarm-card hs-alarm-card--tile" aria-hidden="true">
      <SkeletonPlaceholder className="hs-skel-chip" />
      <SkeletonText heading width="60%" />
      <SkeletonText width="80%" />
    </div>
  );
}

export function AlarmList({ siteId, tab }: { siteId?: string; tab: AlarmTabKey }) {
  const navigate = useNavigate();
  // Atomic selectors so a change to one control doesn't re-render on the others.
  const layout = useAlarmsViewStore(s => s.layout);
  const sort = useAlarmsViewStore(s => s.sort);
  const [{ group }, patchUrl] = useUrlState({ group: 'all' });

  const isAnomalies = tab === 'anomalies';
  const consoleQuery = useConsoleAlarms(siteId);
  const anomaliesQuery = useAnomalies(siteId);
  const source = isAnomalies ? anomaliesQuery : consoleQuery;
  const { isLoading, isError, error, refetch, isFetching } = source;

  const [visibleCount, setVisibleCount] = useState(LIST_PAGE_STEP);
  useEffect(() => setVisibleCount(LIST_PAGE_STEP), [siteId, tab]);

  // Chips are AEM equipment types: the raw value is the filter key, the display
  // name (utils/equipmentTypeLabels) only labels it.
  const tabAlarms = useMemo(
    () => (isAnomalies ? anomaliesQuery.items : (consoleQuery.data?.byTab[tab] ?? [])),
    [isAnomalies, anomaliesQuery.items, consoleQuery.data, tab]
  );

  const groups = useMemo(
    () =>
      groupsOf(tabAlarms).sort((x, y) =>
        equipmentTypeDisplayName(x).localeCompare(equipmentTypeDisplayName(y))
      ),
    [tabAlarms]
  );

  const sorted = useMemo(
    () => sortAlarms(applyGroup(tabAlarms, group), sort),
    [tabAlarms, group, sort]
  );
  const visible = useMemo(() => sorted.slice(0, visibleCount), [sorted, visibleCount]);

  const canLoadMore = visibleCount < sorted.length;
  const loadMore = () => setVisibleCount(count => count + LIST_PAGE_STEP);

  useEffect(() => {
    if (group !== 'all' && groups.length > 0 && !groups.includes(group)) {
      patchUrl({ group: null });
    }
  }, [group, groups, patchUrl]);

  if (isLoading) {
    return (
      <div className="hs-alarm-grid">
        {Array.from({ length: 6 }).map((_, i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // A failed poll keeps react-query's last good `data`; only a failure with
  // nothing to show replaces the list. Otherwise the list stays, marked stale.
  const noun = isAnomalies ? 'anomalies' : 'alarms';
  if (isError && source.data === undefined) {
    return (
      <ActionableNotification
        kind="error"
        lowContrast
        title={isAnomalies ? "Couldn't load anomalies" : "Couldn't load alarms"}
        subtitle={(error as Error)?.message ?? 'Request failed'}
        actionButtonLabel="Retry"
        onActionButtonClick={() => refetch()}
        hideCloseButton
      />
    );
  }

  const staleNotice = isError ? (
    <StaleDataNotice what={noun} error={error} onRetry={() => refetch()} />
  ) : null;

  if (tabAlarms.length === 0) {
    return (
      <>
        {staleNotice}
        <EmptyState
          big="Nothing here right now."
          sub="Normal stays invisible — only exceptions surface at this level."
        />
      </>
    );
  }

  return (
    <>
      {staleNotice}
      <AssetTypeChips
        label="Filter by equipment type"
        chips={groups.map(g => ({ type: g, count: tabAlarms.filter(a => a.group === g).length }))}
        labelOf={equipmentTypeDisplayName}
        total={tabAlarms.length}
        value={group}
        onChange={g => patchUrl({ group: g === 'all' ? null : g })}
      >
        {isFetching && <span className="hs-alarm-fetching">Refreshing…</span>}
      </AssetTypeChips>

      {visible.length === 0 ? (
        <EmptyState big="No alarms in this group." />
      ) : (
        <div className={layout === 'tiles' ? 'hs-alarm-grid' : 'hs-alarm-rows'}>
          {visible.map(a => (
            <AlarmCard
              key={a.uid}
              alarm={a}
              layout={layout}
              onOpen={() => navigate(isAnomalies ? anomalyDetailPath(a) : alarmDetailPath(a))}
            />
          ))}
        </div>
      )}

      {canLoadMore && (
        <div className="hs-alarm-load-more">
          <Button kind="ghost" size="sm" onClick={loadMore}>
            Load more ({sorted.length - visible.length} remaining)
          </Button>
        </div>
      )}
    </>
  );
}

export default AlarmList;
